import { ComponentType, useMemo } from 'react';

import { Redirect, Route, Switch } from 'react-router-dom';

import { Path } from './constants';
import PatientLayout from '~/components/maud/patients/profile/PatientLayout';
import PageNotFound from '~/pages/404';
import { usePatientTabsCollection } from '~/lib/hooks/usePatientTabsCollection';
import { useGlobalContext } from '~/lib/contexts/GlobalContext';

import OverviewPage from '~/pages/maud/patients/[patientId]/overview';
import ProfilePage from '~/pages/maud/patients/[patientId]/profile';
import ClinicalProfilePage from '~/pages/maud/patients/[patientId]/clinical-profile';
import MedicationsPage from '~/pages/maud/patients/[patientId]/medications';
import LabsPage from '~/pages/maud/patients/[patientId]/labs';
import JournalPage from '~/pages/maud/patients/[patientId]/journal';
import AssessmentsPage from '~/pages/maud/patients/[patientId]/assessments';
import EnrollmentPage from '~/pages/maud/patients/[patientId]/enrollment';
import CarePlanningPage from '~/pages/maud/patients/[patientId]/care-planning';
import TasksPage from '~/pages/maud/patients/[patientId]/tasks';
import AppointmentsPage from '~/pages/maud/patients/[patientId]/appointments';
import MessagesPage from '~/pages/maud/patients/[patientId]/messages';
import NotesPage from '~/pages/maud/patients/[patientId]/notes';
import DocumentsPage from '~/pages/maud/patients/[patientId]/documents';
import ActivityPage from '~/pages/maud/patients/[patientId]/activity';

const BASE = `${Path.MAUD_PATIENTS}/:patientId`;

const ROUTE_MAP: Record<string, { component: ComponentType; exact?: boolean }> =
  {
    overview: { component: OverviewPage, exact: true },
    profile: { component: ProfilePage, exact: true },
    'clinical-profile': { component: ClinicalProfilePage, exact: true },
    medications: { component: MedicationsPage, exact: true },
    labs: { component: LabsPage, exact: false },
    journal: { component: JournalPage, exact: true },
    assessments: { component: AssessmentsPage, exact: true },
    enrollment: { component: EnrollmentPage, exact: true },
    'care-planning': { component: CarePlanningPage, exact: false },
    tasks: { component: TasksPage, exact: false },
    appointments: { component: AppointmentsPage, exact: false },
    messages: { component: MessagesPage, exact: true },
    notes: { component: NotesPage, exact: true },
    documents: { component: DocumentsPage, exact: true },
    activity: { component: ActivityPage, exact: true },
  };

const PatientRoutes = () => {
  const { currentUser } = useGlobalContext();
  const tabsCollection = usePatientTabsCollection(
    currentUser?.providerTypeDetails?.name,
  );

  const visibleRoutes = useMemo(() => {
    const visibleSubPaths = new Set(
      Object.values(tabsCollection).map((tab) => tab.subPath),
    );
    return Object.entries(ROUTE_MAP).filter(([subPath]) =>
      visibleSubPaths.has(subPath),
    );
  }, [tabsCollection]);

  return (
    <PatientLayout>
      <Switch>
        <Route
          exact
          path={BASE}
          render={({ match }) => <Redirect to={`${match.url}/overview`} />}
        />
        {visibleRoutes.map(([subPath, { component, exact }]) => (
          <Route
            key={subPath}
            exact={exact}
            path={`${BASE}/${subPath}`}
            component={component}
          />
        ))}
        <Route component={PageNotFound} />
      </Switch>
    </PatientLayout>
  );
};

export default PatientRoutes;
